import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { format, parseISO } from 'date-fns';
import { forkJoin } from 'rxjs';
import { CountryVm } from 'src/app/api';
import { PhonePrefixesService } from 'src/app/api/api/phonePrefixes.service';
import { PhonePrefixesVm } from 'src/app/api/model/phonePrefixesVm';
import { CombinedStore } from 'src/app/core/combined.store';
import { CANAL_VENTE_C } from 'src/app/shared/constants/context.constants';
import { ERROR_EMAIL_PATH } from 'src/app/shared/constants/route.constants';
import { PathType } from 'src/app/shared/enum/path-type.enum';
import { environment } from 'src/environments/environment';
import { HmBrand, isPartner } from '../utils/brand-utils';
import { dispatchDeclarationFromContext } from '../utils/dispatch-utils';
import { ContextsService } from './../../api/api/contexts.service';
import { civiliteObject, Civility } from './../models/model/concretes/shared/civility.model';

@Injectable({
  providedIn: 'root',
})
export class ContextGuard implements CanActivate {
  private store = inject(CombinedStore);

  civilities: Civility[] = civiliteObject;
  constructor(
    private router: Router,
    private contextService: ContextsService,
    private phonePrefixesService: PhonePrefixesService,
  ) {}

  canActivate(route: ActivatedRouteSnapshot): boolean {
    const contextId = route.params['id'];
    if (!environment.production) {
      console.log('ContextResolver: resolve', contextId);
    }
    forkJoin([this.contextService.apiContextsIdGet(contextId), this.phonePrefixesService.apiPhonePrefixesGet()])
      .pipe()
      .subscribe(([ctx, prefixes]) => {
        if (ctx) {
          console.log('context new ', ctx);
          sessionStorage.clear();
          this.store.reset();
          this.store.updateContext(ctx);
          this.store.updateContext({
            token: contextId,
          });

          if (this.store.context().souscripteur?.email) {
            this.updateSubscriberWithContext(prefixes);
          } else if (this.store.context().canalVente === CANAL_VENTE_C && isPartner(environment.brand)) {
            // In partner mode, if no email in context, redirect to error with specific message
            this.router.navigate(['/' + ERROR_EMAIL_PATH]);
            return;
          }

          // Update Path before declaration dispatch to compute path-specific defaults correctly.
          this.store.updatePathType(ctx.produit as PathType);
          if (ctx.declaration) {
            dispatchDeclarationFromContext(this.store, ctx.declaration);
          }
          // Redirection
          this.router.navigate(['/start', ctx.token]);
          return true;
        }
        // Empêche l'accès au composant cible après redirection
        return false;
      });
    return false;
  }

  private updateSubscriberWithContext(prefixes: PhonePrefixesVm[]) {
    const dateString = this.store.context().souscripteur?.dateNaissance ?? '';
    const date = dateString != '' ? parseISO(dateString) : '';
    const formattedDate = dateString != '' ? format(date, 'dd/MM/yyyy') : '';
    let phone = '';
    let indicator = '';

    if (this.store.context().souscripteur?.telephone && this.store.context().codeVendeur !== HmBrand) {
      phone = this.store.context().souscripteur?.telephone ?? '';
      // check on string if there is a + in the string for indicator
      if (phone.startsWith('+')) {
        // we slice the next 2/3 characters for completion
        indicator = phone.slice(0, 3);
        // remove indicator in phone number
        phone = phone.slice(3);
        // France numbers can be provided as +330XXXXXXXXX, but the form expects XXXXXXXXX.
        if (indicator === '+33' && phone.startsWith('0')) {
          phone = phone.slice(1);
        }
      } else {
        // No prefix provided: default to France (+33)
        indicator = '+33';
        // Strip leading '0' if present (France local format: 0XXXXXXXXX → XXXXXXXXX)
        if (phone.startsWith('0')) {
          phone = phone.slice(1);
        }
      }
    }

    const indicatorVm: PhonePrefixesVm | null = prefixes.find((p) => p.prefix === indicator) ?? null;
    this.store.updateSubscriber({
      email: this.store.context().souscripteur?.email ?? '',
      civility: this.civilities.find((m) => m.id == this.store.context().souscripteur?.civilite) ?? null,
      lastname: this.store.context().souscripteur?.nom ?? '',
      firstname: this.store.context().souscripteur?.prenom ?? '',
      birth_date: formattedDate,
      birth_postal_code: this.store.context().souscripteur?.codePostalNaissance ?? null,
      birth_city: this.store.context().souscripteur?.villeNaissance ?? '',
      birth_country: this.mapToCountryVm(this.store.context().souscripteur?.paysNaissance ?? '') ?? null,
      nationality: this.mapToCountryVm(this.store.context().souscripteur?.nationalite ?? '') ?? null,
      phone_prefix: {
        indicatorString: indicator,
        indicator: indicatorVm,
        phone: phone,
        phone_consent: false,
      },
      phone_number: phone,
      consent: {
        marketing_phone_consent: false,
        marketing_email_consent: false,
      },
      is_client: false,
      from_context: true,
    });

    if (this.store.context().souscripteur?.adresse) {
      const adresse = this.store.context().souscripteur?.adresse ?? '';
      const codePostal = this.store.context().souscripteur?.codePostal ?? '';
      const ville = this.store.context().souscripteur?.ville ?? '';
      this.store.updateAddress({
        addressAutocomplete: {
          place: `${adresse}, ${codePostal} ${ville}`,
          streetNumber: adresse.split(' ').slice(0, 1).join(' '),
          street: adresse.split(' ').slice(1).join(' '),
          zipCode: codePostal,
          city: ville,
        },
        address: this.store.context().souscripteur?.adresse ?? '',
        city: this.store.context().souscripteur?.ville ?? '',
        country: this.mapToCountryVm(this.store.context().souscripteur?.pays ?? ''),
        zipCode: this.store.context().souscripteur?.codePostal ?? '',
      });
    }
  }

  private mapToCountryVm(countryName: string | undefined): CountryVm | null {
    if (!countryName) {
      return null;
    }

    return {
      countryCode: countryName == 'France' ? 'FR' : null,
      countryNameFr: countryName,
    };
  }
}
